import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { BranchQuoteSupportStatus, BranchQuoteSupportItemType } from '../../api/dealer-api-interface-franchisee';
import { deriveSupportCapabilities } from './support-capabilities';
import { SupportTicketsDelta, SupportTicketService, SupportTicketsState } from './support-ticket-service';
import { LangBranchQuoteConversation } from '../../../localization/scripts/dealer-franchisee/conversation/wm-branchquote-conversation-lang';
import { FranchiseeQuoteContainerManager } from '../quotes/data/franchisee-quote-manager';
import { AskConfirmation } from '../../../webmodule-common/other/ui/modal-confirmation';

/**
 * Support Ticket Actions (replacement for legacy buildSupportActions factory)
 * Renders status transition buttons (issue, resolve, cancel, reactivate) with identical UI output.
 * Hides buttons when they are not applicable (mirroring previous hideWhenDisabled=true usage).
 */
@customElement('support-ticket-actions')
export class SupportTicketActions extends LitElement {
  // ticket property removed; component now works from ticketId + service snapshot
  @property({ type: String }) ticketId?: string;
  // Use unknown to avoid nominal/private member identity mismatch when multiple module paths load the manager class.
  // Cast internally where needed.
  @property({ attribute: false }) quoteManager?: FranchiseeQuoteContainerManager;
  @property({ type: Boolean }) readonly = false;
  @property({ type: Boolean }) forceReadonly = false;
  /** If true, non-applicable buttons are fully hidden (matches prior hideWhenDisabled) */
  @property({ type: Boolean }) hideWhenDisabled = true;
  /** Optimistic local status patch (keeps former immediate UX); set false to disable */
  @property({ type: Boolean }) optimistic = false; // disabled – state now driven by service snapshots

  private unsubscribeService?: () => void;

  static styles = css`
    :host {
      display: inline-flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
  `;

  connectedCallback() {
    super.connectedCallback();
    this.subscribeToService();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubscribeFromService();
  }

  private subscribeToService() {
    this.unsubscribeFromService();
    const svc = this.service;
    if (svc) {
      this.unsubscribeService = svc.subscribe((state: SupportTicketsState, delta: SupportTicketsDelta) => {
        let currentTicketId = this.ticketId || null;
        if (!currentTicketId) {
          const ticketIds = Object.keys(state.tickets || {});
          if (ticketIds.length === 1) currentTicketId = ticketIds[0];
        }
        // Session-centric draft tracking: any draftTicketIds present are active draft sessions.
        const draftIds = state.draftTicketIds || [];
        const draftId = draftIds.length === 1 ? draftIds[0] : undefined;
        const affected =
          (currentTicketId && delta.changedTicketIds.includes(currentTicketId)) ||
          delta.changedTicketIds.length === 0 ||
          (!currentTicketId && draftId) ||
          (currentTicketId && draftIds.includes(currentTicketId));
        if (affected) this.requestUpdate();
      });
    }
  }

  private unsubscribeFromService() {
    if (this.unsubscribeService) {
      this.unsubscribeService();
      this.unsubscribeService = undefined;
    }
  }

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    // Re-subscribe if quoteManager changed (service might be different)
    if (changedProperties.has('quoteManager')) {
      this.subscribeToService();
    }
  }

  private get service(): SupportTicketService | null {
    const qm = this.quoteManager as FranchiseeQuoteContainerManager | undefined;
    return qm?.supportTicketService ?? null;
  }

  private getCurrentTicketDetail(): {
    id: string | null;
    status: BranchQuoteSupportStatus | undefined;
    type: BranchQuoteSupportItemType | undefined;
    isDraftNew: boolean; // true when service is in new-ticket draft workflow for this id
  } {
    const svc = this.service;
    if (!svc) return { id: null, status: undefined, type: undefined, isDraftNew: false };
    // Determine candidate id: explicit ticketId, else single draft, else single ticket snapshot.
    const state: SupportTicketsState = svc.getState();
    let id: string | null = this.ticketId || null;
    if (!id && state.draftTicketIds?.length === 1) id = state.draftTicketIds[0];
    if (!id) {
      const ticketIds = Object.keys(state.tickets || {});
      if (ticketIds.length === 1) id = ticketIds[0];
    }
    if (!id) return { id: null, status: undefined, type: undefined, isDraftNew: false };
    try {
      const session: any = svc.getSession(id);
      const isDraft = session?.kind === 'draft';
      const type: BranchQuoteSupportItemType | undefined = session?.supportType ?? session?.ticket?.type;
      const status: BranchQuoteSupportStatus | undefined =
        session?.ticket?.status || (isDraft ? BranchQuoteSupportStatus.Draft : undefined);
      return { id, status, type, isDraftNew: isDraft };
    } catch {
      return { id, status: undefined, type: undefined, isDraftNew: false };
    }
  }

  private canActions() {
    const { id, status, type, isDraftNew } = this.getCurrentTicketDetail();
    const qm = this.quoteManager as FranchiseeQuoteContainerManager | undefined;
    const caps = deriveSupportCapabilities({
      quoteManager: qm as FranchiseeQuoteContainerManager,
      ticketStatus: status,
      forceReadonly: this.forceReadonly,
      transientBusy: false,
      phase: 'editor'
    });
    if (!status) {
      return { caps, canIssue: false, canResolve: false, canCancel: false, canReactivate: false };
    }
    const terminal = status === BranchQuoteSupportStatus.Cancelled || status === BranchQuoteSupportStatus.Resolved;
    const isQuoteReview = type === BranchQuoteSupportItemType.QuoteReview;
    const isDraft = status === BranchQuoteSupportStatus.Draft && (isDraftNew || !terminal);
    const ro = this.readonly || this.forceReadonly;
    let hasUserComment = true;
    try {
      const svc = this.service;
      if (svc && id && !isDraftNew) {
        const session = svc.getSession(id);
        const entries = session.entries;
        if (Array.isArray(entries) && entries.length > 0) {
          hasUserComment = entries.some(
            (e: any) => e && e.text && e.text !== 'MASTERDOCUMENT' && !(e.systemNote || e.systemnote)
          );
          if (!hasUserComment) hasUserComment = true; // relaxed policy
        }
      }
    } catch {
      hasUserComment = true;
    }
    const canIssue = !ro && !isQuoteReview && isDraft && caps.actions.changeStatus && hasUserComment;
    // Save Draft enabled only while ticket status is Draft (new workflow or persisted draft editing)
    const canSaveDraft = !ro && isDraft;
    // Delete draft button only for standard Draft tickets (exclude QuoteReview & EngineeredToOrder)
    const canDeleteDraft = !ro && isDraft && !isQuoteReview && type !== BranchQuoteSupportItemType.EngineeredToOrder;
    const canResolve = !ro && !isQuoteReview && !isDraft && !terminal && caps.actions.resolve;
    const canCancel = !ro && !isQuoteReview && !isDraft && !terminal && caps.actions.cancel;
    const canReactivate = !isQuoteReview && caps.actions.reactivate;
    return { caps, canIssue, canResolve, canCancel, canReactivate, canSaveDraft, canDeleteDraft };
  }

  private async changeStatus(next: BranchQuoteSupportStatus) {
    const { id, status } = this.getCurrentTicketDetail();
    if (!id || !status) return;
    if (!(await this._preStatusChangeVeto())) return;

    const detail = {
      ticketId: id,
      nextStatus: next
    } as any;
    this.dispatchEvent(
      new CustomEvent('support:intent:status-change', {
        detail,
        bubbles: true,
        composed: true,
        cancelable: false
      })
    );
  }

  /** Dispatches an async veto event allowing external listeners to cancel the status change. */
  private async _preStatusChangeVeto(): Promise<boolean> {
    try {
      const detail: { canChange: Promise<boolean> } = { canChange: Promise.resolve(true) };
      this.dispatchEvent(
        new CustomEvent('support-ticket-before-status-change', {
          detail,
          bubbles: true,
          composed: true,
          cancelable: false
        })
      );
      try {
        const allowed = await detail.canChange;
        return !!allowed;
      } catch {
        return false; // rejection => veto
      }
    } catch {
      return true; // fail-open on dispatch errors
    }
  }

  private renderButton(label: string, variant: string, disabled: boolean, onClick: () => void) {
    if (this.hideWhenDisabled && disabled) return html``;
    return html`<webmodule-button size="small" variant=${variant as any} ?disabled=${disabled} @click=${onClick}
      >${label}</webmodule-button
    >`;
  }

  private async _saveDraft() {
    const svc = this.service;
    if (!svc) return;
    const { id, status } = this.getCurrentTicketDetail();
    if (!id || status !== BranchQuoteSupportStatus.Draft) return;
    try {
      await svc.saveTicket(id, { finalizeDraftEntry: false });
    } catch (err) {
      // Fail silently; could add toast/logging later
    }
  }

  render() {
    const { canIssue, canResolve, canCancel, canReactivate, canSaveDraft, canDeleteDraft } = this.canActions();
    return html`
      ${this.renderButton(LangBranchQuoteConversation.saveDraft(), 'default', !canSaveDraft, () => this._saveDraft())}
      ${this.renderButton(LangBranchQuoteConversation.deleteDraft(), 'default', !canDeleteDraft, () =>
        this._deleteDraft()
      )}
      ${this.renderButton(LangBranchQuoteConversation.issueTicket(), 'primary', !canIssue, () =>
        this.changeStatus(BranchQuoteSupportStatus.New)
      )}
      ${this.renderButton(LangBranchQuoteConversation.resolveTicket(), 'primary', !canResolve, () =>
        this.changeStatus(BranchQuoteSupportStatus.Resolved)
      )}
      ${this.renderButton(LangBranchQuoteConversation.cancelTicket(), 'default', !canCancel, () =>
        this.changeStatus(BranchQuoteSupportStatus.Cancelled)
      )}
      ${this.renderButton(LangBranchQuoteConversation.reactivateTicket(), 'primary', !canReactivate, () =>
        this.changeStatus(BranchQuoteSupportStatus.ReActivated)
      )}
    `;
  }
  /** Delete draft ticket (transient or persisted) then emit UI intent so parent can close/abandon editor. */
  private async _deleteDraft(): Promise<void> {
    if (!(await AskConfirmation(LangBranchQuoteConversation.areYouSureDeleteDraft()))) return;
    const svc = this.service;
    if (!svc) return;
    const { id, status } = this.getCurrentTicketDetail();
    if (!id || status !== BranchQuoteSupportStatus.Draft) return; // only drafts deletable
    let success = false;
    let persisted = false;
    try {
      const session: any = svc.getSession(id);
      persisted = !!session?.ticket; // persisted draft has ticket snapshot
      success = await svc.deleteTicket(id);
    } catch {
      success = false;
    }
    // Emit UI intent regardless of success (listener may show error or keep editor open)
    const detail = { ticketId: id, persisted, success };
    this.dispatchEvent(
      new CustomEvent('support:intent:delete-draft', {
        detail,
        bubbles: true,
        composed: true,
        cancelable: false
      })
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'support-ticket-actions': SupportTicketActions;
  }
}
